import { useState } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  DataTable,
  StatCard,
  StatusBadge,
  SelectInput,
  type Column,
  type StatusType,
} from '@themes/aegix';
import {
  HiCheckCircle,
  HiExclamationTriangle,
  HiXCircle,
} from 'react-icons/hi2';
import {
  useEntitlements,
  useLicenses,
  useSeatAssignments,
  useLicenseTemplates,
  useSetLicenseStatus,
  useCreateLicenseFromTemplate,
} from '../../hooks/queries/useLicensing';
import { useActiveOrganization } from '@aegix/auth-organization';
import type { ActiveOrganization } from '../../lib/sms-auth';
import type {
  License,
  LicenseStatus,
  LicenseStatusAction,
  LicenseTemplate,
} from '../../services/licensing.service';
import { useHasPermissions } from '../../utils/hasPermission';

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_MAP: Partial<
  Record<string, { label: string; status: StatusType }>
> = {
  trial: { label: 'Trial', status: 'info' },
  active: { label: 'Active', status: 'success' },
  grace: { label: 'Grace Period', status: 'warning' },
  expired: { label: 'Expired', status: 'error' },
  suspended: { label: 'Suspended', status: 'error' },
  revoked: { label: 'Revoked', status: 'error' },
  purged: { label: 'Purged', status: 'error' },
};

function formatDate(iso: string | null): string {
  if (!iso) return 'No expiry';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const MANAGE_LICENSE = ['licensing.manage_license'];

// ── Sub-components ───────────────────────────────────────────────────────────

const STATUS_ACTIONS: Record<LicenseStatus, LicenseStatusAction[]> = {
  trial: ['activate', 'suspend'],
  active: ['suspend', 'renew'],
  grace: ['activate', 'suspend', 'renew'],
  suspended: ['activate', 'revoke'],
  expired: ['renew', 'revoke'],
  revoked: [],
  purged: [],
};

const ACTION_LABELS: Record<LicenseStatusAction, string> = {
  activate: 'Activate',
  suspend: 'Suspend',
  revoke: 'Revoke',
  renew: 'Renew',
};

function LicenseCardActions({ license }: { license: License }) {
  const setStatus = useSetLicenseStatus();
  const actions = STATUS_ACTIONS[license.status] ?? [];
  if (actions.length === 0) return null;

  const handle = async (action: LicenseStatusAction) => {
    const verb =
      action === 'revoke'
        ? 'Revoke this license? Begins the data lifecycle.'
        : action === 'renew'
          ? 'Enter new expiry date (ISO):'
          : null;
    let body: Record<string, unknown> = {};
    if (action === 'renew') {
      const expires_at = window.prompt(verb!, license.expires_at ?? '');
      if (!expires_at) return;
      body = { expires_at };
    } else if (action === 'revoke') {
      if (!window.confirm(verb!)) return;
    }
    await setStatus.mutateAsync({ licenseId: license.id, action, body });
  };

  return (
    <HStack
      gap={2}
      mt={3}
      pt={3}
      borderTop="1px solid"
      borderColor="border.primary"
    >
      {actions.map((action) => (
        <Button
          key={action}
          size="xs"
          variant={action === 'revoke' ? 'outline' : 'subtle'}
          onClick={() => handle(action)}
          loading={setStatus.isPending}
        >
          {ACTION_LABELS[action]}
        </Button>
      ))}
    </HStack>
  );
}

function LicenseCard({
  license,
  canManage,
}: {
  license: License;
  canManage: boolean;
}) {
  const statusInfo = STATUS_MAP[license.status] ?? {
    label: license.status,
    status: 'info' as StatusType,
  };
  const remaining = daysUntil(license.expires_at);
  const enabledModules = license.license_modules.filter((m) => m.enabled);

  return (
    <Box
      bg="surface.primary"
      border="1px solid"
      borderColor="border.primary"
      borderRadius="lg"
      p={5}
    >
      <Flex justify="space-between" align="start" mb={4}>
        <Box>
          <Text fontWeight={600} fontSize="md" color="text.primary">
            {license.product_name}
          </Text>
          <Text fontSize="sm" color="text.secondary">
            {license.organization_name}
          </Text>
        </Box>
        <StatusBadge label={statusInfo.label} status={statusInfo.status} />
      </Flex>

      {/* Degraded warning */}
      {license.is_degraded && (
        <Flex
          bg="yellow.50"
          border="1px solid"
          borderColor="yellow.200"
          borderRadius="md"
          p={3}
          mb={4}
          align="center"
          gap={2}
        >
          <HiExclamationTriangle color="var(--chakra-colors-yellow-500)" />
          <Text fontSize="sm" color="yellow.800">
            This license is in degraded mode. Some features may be read-only.
          </Text>
        </Flex>
      )}

      <SimpleGrid columns={2} gap={3} mb={4}>
        <Box>
          <Text fontSize="xs" color="text.secondary">
            Starts
          </Text>
          <Text fontSize="sm" fontWeight={500} color="text.primary">
            {formatDate(license.starts_at)}
          </Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="text.secondary">
            Expires
          </Text>
          <Text fontSize="sm" fontWeight={500} color="text.primary">
            {formatDate(license.expires_at)}
            {remaining !== null && remaining > 0 && remaining <= 30 && (
              <Text as="span" fontSize="xs" color="text.danger" ml={1}>
                ({remaining}d remaining)
              </Text>
            )}
          </Text>
        </Box>
        {license.max_seats && (
          <Box>
            <Text fontSize="xs" color="text.secondary">
              Seats
            </Text>
            <Text fontSize="sm" fontWeight={500} color="text.primary">
              {license.max_seats} max
            </Text>
          </Box>
        )}
        <Box>
          <Text fontSize="xs" color="text.secondary">
            Auto-Renew
          </Text>
          <Text fontSize="sm" fontWeight={500} color="text.primary">
            {license.auto_renew ? 'Yes' : 'No'}
          </Text>
        </Box>
      </SimpleGrid>

      {/* Enabled modules */}
      {enabledModules.length > 0 && (
        <Box pt={3} borderTop="1px solid" borderColor="border.primary">
          <Text fontSize="xs" fontWeight={600} color="text.secondary" mb={2}>
            Enabled Modules
          </Text>
          <Flex gap={2} flexWrap="wrap">
            {enabledModules.map((m) => (
              <Flex
                key={m.module_code}
                align="center"
                gap={1}
                px={2}
                py={1}
                bg="surface.secondary"
                borderRadius="md"
                fontSize="xs"
                color="text.primary"
              >
                <HiCheckCircle
                  color="var(--chakra-colors-green-500)"
                  size={12}
                />
                {m.module_name}
              </Flex>
            ))}
          </Flex>
        </Box>
      )}

      {/* Seat assignments (for user-based licenses) */}
      {license.max_seats && <SeatAssignmentsTable licenseId={license.id} />}

      {/* License key (partial) */}
      {license.license_key && (
        <Box mt={3} pt={3} borderTop="1px solid" borderColor="border.primary">
          <Text fontSize="xs" color="text.tertiary">
            License Key: {license.license_key.slice(0, 8)}...
          </Text>
        </Box>
      )}

      {/* Manager actions (ADR 104 §18.7) */}
      {canManage && <LicenseCardActions license={license} />}
    </Box>
  );
}

// ── Re-apply from template (ADR 104 §18.5) ──────────────────────────────────

function ApplyTemplateForm({
  organizationId,
  organizationName,
  onClose,
}: {
  organizationId: number;
  organizationName: string;
  onClose: () => void;
}) {
  const { data: templates, isLoading } = useLicenseTemplates(true);
  const apply = useCreateLicenseFromTemplate();
  const [selected, setSelected] = useState<string>('');

  const submit = async () => {
    if (!selected) return;
    await apply.mutateAsync({
      organization_id: organizationId,
      template_id: selected,
    });
    onClose();
  };

  return (
    <Box
      bg="surface.primary"
      border="1px solid"
      borderColor="border.primary"
      borderRadius="lg"
      p={4}
      mb={6}
    >
      <Text fontWeight={600} mb={1}>
        Apply a license template to {organizationName}
      </Text>
      <Text fontSize="sm" color="text.secondary" mb={3}>
        Creates a License from the template's defaults. Idempotent — if a
        License for this product already exists, the existing one is returned
        unchanged.
      </Text>
      {isLoading ? (
        <Spinner size="sm" />
      ) : (
        <HStack gap={3} align="end">
          <Box flex="1">
            <SelectInput
              id="license-template"
              name="license-template"
              value={selected}
              label="License Template"
              placeholder="Select a template"
              option={(templates ?? []).map((t: LicenseTemplate) => ({
                value: t.id,
                label: `${t.name} (${t.vendor_name ?? 'Aegix-global'} · ${t.product_code.toUpperCase()})`,
              }))}
              onChange={(val) => setSelected(val)}
              invalid={apply.isError}
              error={
                apply.isError
                  ? ((apply.error as Error)?.message ?? 'Failed to apply template')
                  : undefined
              }
              e2eTitle="license-template-select"
            />
          </Box>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!selected || apply.isPending}
            loading={apply.isPending}
          >
            Apply
          </Button>
        </HStack>
      )}
    </Box>
  );
}

// ── Seat Assignments ────────────────────────────────────────────────────────

interface SeatRow {
  id: number;
  user: number;
  assigned_at: string;
  released_at: string;
}

const seatColumns: Column<SeatRow>[] = [
  { key: 'user', label: 'User ID' },
  {
    key: 'assigned_at',
    label: 'Assigned',
    render: (row) => new Date(row.assigned_at).toLocaleDateString(),
  },
  {
    key: 'released_at',
    label: 'Status',
    render: (row) =>
      row.released_at ? (
        <StatusBadge label="Released" status="warning" />
      ) : (
        <StatusBadge label="Active" status="success" />
      ),
  },
];

function SeatAssignmentsTable({ licenseId }: { licenseId: number }) {
  const { data, isLoading } = useSeatAssignments(licenseId);

  if (isLoading) return <Spinner size="sm" />;
  if (!data || data.length === 0) return null;

  const rows: SeatRow[] = data.map((s) => ({
    id: s.id,
    user: s.user,
    assigned_at: s.assigned_at,
    released_at: s.released_at ?? '',
  }));

  return (
    <Box mt={3} pt={3} borderTop="1px solid" borderColor="border.primary">
      <Text fontSize="xs" fontWeight={600} color="text.secondary" mb={2}>
        Seat Assignments ({data.filter((s) => !s.released_at).length} /{' '}
        {data.length})
      </Text>
      <DataTable
        columns={seatColumns}
        data={rows}
        keyExtractor={(row) => String(row.id)}
      />
    </Box>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export function BillingContent() {
  const canManage = useHasPermissions(MANAGE_LICENSE);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const {
    data: entitlements,
    isLoading: entLoading,
    error: entError,
  } = useEntitlements();
  const {
    data: licensesData,
    isLoading: licLoading,
    error: licError,
  } = useLicenses({ page_size: 50 });

  const isLoading = entLoading || licLoading;
  const error = entError || licError;

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={12}>
        <Text color="text.danger">
          {error instanceof Error
            ? error.message
            : 'Failed to load billing data'}
        </Text>
      </Center>
    );
  }

  const licenses = licensesData?.results ?? [];
  const enabledModuleCount = entitlements
    ? Object.values(entitlements.modules).filter(Boolean).length
    : 0;
  const totalModuleCount = entitlements
    ? Object.keys(entitlements.modules).length
    : 0;

  const firstOrg = licenses[0];
  const activeOrganization = useActiveOrganization() as ActiveOrganization | null;
  const targetOrgId = activeOrganization?.id ?? firstOrg?.organization;
  const targetOrgName = activeOrganization?.name ?? firstOrg?.organization_name ?? '';

  return (
    <Box px={{ base: 4, md: 8 }} py={6}>
      <Flex justify="space-between" align="start" mb={2}>
        <Box>
          <Text fontSize="lg" fontWeight={600} color="text.primary">
            Billing &amp; Licensing
          </Text>
          <Text fontSize="sm" color="text.secondary">
            License status, module entitlements, and subscription details for
            your organization.
          </Text>
        </Box>
        {canManage && !showApplyTemplate && (
          <Button size="sm" onClick={() => setShowApplyTemplate(true)}>
            {licenses.length === 0
              ? 'Create license from template'
              : 'Re-apply from template'}
          </Button>
        )}
      </Flex>

      {showApplyTemplate && targetOrgId && (
        <Box mt={4}>
          <ApplyTemplateForm
            organizationId={targetOrgId}
            organizationName={targetOrgName}
            onClose={() => setShowApplyTemplate(false)}
          />
        </Box>
      )}
      <Box mb={6} />

      {/* Entitlements summary */}
      {entitlements && (
        <SimpleGrid columns={{ base: 2, md: 4 }} gap={4} mb={6}>
          <StatCard
            label="License Status"
            value={
              STATUS_MAP[entitlements.status]?.label ?? entitlements.status
            }
          />
          <StatCard
            label="Product"
            value={entitlements.product?.toUpperCase()}
          />
          <StatCard
            label="Modules"
            value={`${enabledModuleCount} / ${totalModuleCount}`}
          />
          <StatCard
            label="Expires"
            value={formatDate(entitlements.expires_at)}
          />
        </SimpleGrid>
      )}

      {/* Degraded mode banner */}
      {entitlements?.is_degraded && (
        <Flex
          bg="yellow.50"
          border="1px solid"
          borderColor="yellow.200"
          borderRadius="lg"
          p={4}
          mb={6}
          align="center"
          gap={3}
        >
          <HiExclamationTriangle
            color="var(--chakra-colors-yellow-500)"
            size={24}
          />
          <Box>
            <Text fontWeight={600} color="yellow.800">
              Degraded Mode
            </Text>
            <Text fontSize="sm" color="yellow.700">
              Your license is in {entitlements.status} status. Some features are
              restricted to read-only access. Contact your administrator to
              renew.
            </Text>
          </Box>
        </Flex>
      )}

      {/* Module entitlements grid */}
      {entitlements && totalModuleCount > 0 && (
        <Box mb={6}>
          <Text fontWeight={600} color="text.primary" mb={3}>
            Module Entitlements
          </Text>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} gap={3}>
            {Object.entries(entitlements.modules).map(([code, enabled]) => (
              <Flex
                key={code}
                align="center"
                gap={2}
                px={3}
                py={2}
                bg="surface.primary"
                border="1px solid"
                borderColor="border.primary"
                borderRadius="md"
              >
                {enabled ? (
                  <HiCheckCircle color="var(--chakra-colors-green-500)" />
                ) : (
                  <HiXCircle color="var(--chakra-colors-gray-400)" />
                )}
                <Text
                  fontSize="sm"
                  color={enabled ? 'text.primary' : 'text.tertiary'}
                >
                  {code
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </Text>
              </Flex>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* License details */}
      {licenses.length > 0 && (
        <Box>
          <Text fontWeight={600} color="text.primary" mb={3}>
            Licenses
          </Text>
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
            {licenses.map((license) => (
              <LicenseCard
                key={license.id}
                license={license}
                canManage={canManage}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {licenses.length === 0 && !isLoading && (
        <Center py={8}>
          <Text color="text.secondary">No licenses found.</Text>
        </Center>
      )}
    </Box>
  );
}
